// cleanup_convert_to_typescript_file_SMB_EMPLOYEE_PAYMENT
// cleanup_remove_external_libraries_SMB_EMPLOYEE_PAYMENT

import React from 'react';
import { zip, forkJoin, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import moment from 'moment-timezone';
import { isEmpty, truncate } from 'lodash';

import forceServerTz from 'logic/utils/forceServerTimezone';
import {
  getAchCompanyForPaymentType$,
  getACHPaymentSettings$,
  getSMBPaymentTypes$,
  retrieveSMBEmployeePaymentDetails$,
  initiateSMBEmployeePayment$
} from 'api/payments/service';
import { simplifiedPaymentsAction$ } from 'api/smallBusinessPayments/service';
import { getDates, getApprovedEmployeePayees$ } from 'api/sendPayment/service';
import { PAYMENT_TYPES, WORKSPACES, WORKFLOWS, API_ERROR, PAYMENT_ACTIONS } from 'constants.js';
import { numberToDecimal } from 'utils/currency';

import ConfirmModal from './ConfirmModal';

const FORM_SECTIONS = {
  BATCH: 'batch',
  TRANSACTIONS: 'transactions'
};

const generateApprovedTitle = (locale) => (title, approvalRequirement) => {
  if ([1, 2].includes(approvalRequirement)) {
    return locale['employee.payment.requiresApproval'];
  }

  return title;
};

const currencyToNumber = (value) => {
  if (typeof value === 'number') return value;
  if (value === null) return 0;
  const removeCommas = value.replace(/,/g, '');
  return Number(removeCommas);
};

const handleError = ({
  callback,
  data,
  errorMessage,
  locale,
  setChallengeManager,
  setMessage
}) => {
  if (data?.challeangeable || data?.[0]?.code === API_ERROR.ACCOUNT_LOCKED) {
    setChallengeManager({
      callback: () => callback(),
      data
    });
  } else {
    setMessage({
      title: errorMessage || locale['common.error']
    });
  }
  window.scroll(0, 0);
};

const handleServerError = () => {
  // TODO: some sort of logic for server errors.
  // This only runs when server is 500, 404, etc. Not when success is false.
};

const normalizeErrorResp = ({ data, locale, success }) => {
  const limitWarning =
    data.warning && data.warningCode === API_ERROR.PAYMENT_PREFUND_EXCEEDED_DAILY_LIMIT;
  const limitWarningMsg = limitWarning && data.warningMessage ? data.warningMessage : '';

  // if no error anywhere return
  if (success && data.actionSuccess)
    return { errorMessage: '', limitWarning: limitWarningMsg, success };

  // if top level error
  if (!success) {
    const errorMessage =
      Array.isArray(data) &&
      data.reduce((acc, curr) => {
        const { code, userMessage } = curr || {};
        const errorMsg = locale[code] || userMessage;
        return acc !== '' ? `${acc} ${errorMsg}` : errorMsg;
      }, '');

    return {
      errorMessage: errorMessage || locale['common.server.error'],
      limitWarning: limitWarningMsg,
      success
    };
  }

  // if action error
  if (!data.actionSuccess) {
    const errorMessage = data?.actionMessages?.reduce((acc, curr) => {
      const { code, userMessage } = curr || {};
      const errorMsg = locale[code] || userMessage;
      return `${acc} ${errorMsg}`;
    }, '');

    return { errorMessage, limitWarning: limitWarningMsg, success: !errorMessage };
  }

  return { errorMessage: '', limitWarning: '', success };
};

export const runOnSubmit = ({
  // This action variable is coming from the workflow of the page you are on (AKA: create, modify, view, template)
  APIData,
  action,
  classes,
  confirmation,
  hasFormChanged,
  history,
  id,
  locale,
  onChange,
  setChallengeManager,
  setConfirmation,
  setIsSubmitting,
  setMessage,
  // This variable comes from what button the user hit (AKA: back, approve, delete, reject, copy, send)
  setReduxMessage,
  setSmbHighlightedRows,
  submitType,
  totalAmount,
  userSessionInfo,
  values
}) => {
  const serverTimezone = userSessionInfo?.timeZone;

  if (
    [PAYMENT_ACTIONS.APPROVE, PAYMENT_ACTIONS.DELETE, PAYMENT_ACTIONS.REJECT].includes(submitType)
  ) {
    let actionTitle;
    if (submitType === PAYMENT_ACTIONS.APPROVE) actionTitle = locale['common.approvePayment'];
    if (submitType === PAYMENT_ACTIONS.DELETE) actionTitle = locale['common.deletePayment'];
    if (submitType === PAYMENT_ACTIONS.REJECT) actionTitle = locale['common.rejectPayment'];
    const execute = () =>
      simplifiedPaymentsAction$(
        {
          ids: [
            {
              custTrace: id,
              paymentType: locale['common.employee'],
              transactionDt: APIData?.paymentDetails?.effectiveEntryDate
            }
          ]
        },
        // Tell function which endpoint to use (approve, delete, reject)
        submitType
      ).subscribe(({ data, success }) => {
        setIsSubmitting(false);
        setConfirmation(null);
        if (!success) {
          return handleError({
            callback: () => execute(),
            data,
            locale,
            setChallengeManager,
            setMessage
          });
        }
        setChallengeManager(null);

        const { payment: { actionSuccess, bankTrace, custTrace, actionMessages = [] } = {} } =
          data?.[0] || {};
        const paymentActionMessages = actionMessages.reduce(
          (acc, msg) => (acc !== '' ? `${acc} ${msg?.userMessage}` : msg?.userMessage),
          ''
        );

        const detailMessage = `${id}. ${paymentActionMessages}`;
        setReduxMessage({
          details: `${locale['employee.payment.customerTraceNumber']} : ${detailMessage}`,
          success: actionSuccess,
          title: actionSuccess
            ? locale[`payments.success.${submitType}`]
            : locale[`payments.error.${submitType}`],
          type: locale['employee.payment.smallBusiness']
        });
        setSmbHighlightedRows([
          {
            bankTrace,
            custTrace,
            errorHighlighting: !actionSuccess,
            successHighlighting: actionSuccess
          }
        ]);

        return history.push({
          pathname: history?.location?.state?.previous || WORKSPACES.PYMTCNTR
        });
      }, handleServerError);
    if (confirmation) return execute();
    return setConfirmation({
      Component: (
        <ConfirmModal
          action={submitType}
          actionTitle={actionTitle}
          actionCallback={execute}
          apiData={APIData}
          formState={values}
          locale={locale}
          onHide={() => {
            setConfirmation(null);
            setIsSubmitting(false);
          }}
          totalAmount={totalAmount}
          classes={classes}
        />
      ),
      variant: locale['common.error'].toLowerCase()
    });
  }
  if (submitType === locale['common.back'].toLowerCase()) {
    setIsSubmitting(false);
    if (action === PAYMENT_ACTIONS.VIEW || !hasFormChanged) {
      return history.push({
        pathname: history?.location?.state?.previous || WORKSPACES.PYMTCNTR
      });
    }
    return setConfirmation({
      callback: () =>
        history.push({
          pathname: history?.location?.state?.previous || WORKSPACES.PYMTCNTR
        }),
      routeGuard: true
    });
  }
  if (submitType === PAYMENT_ACTIONS.COPY) {
    setIsSubmitting(false);
    history.push({
      pathname: `${WORKFLOWS.COPY_EMPLOYEE_PAYMENT}/${id}`
    });
  }
  if (submitType === PAYMENT_ACTIONS.SEND) {
    const payments = Object.keys(values.transactions)
      // Only filter values from create flow (modify will send 0 amounts for employees)
      .filter((key) =>
        action === PAYMENT_ACTIONS.CREATE
          ? currencyToNumber(values.transactions[key].amount) !== 0
          : true
      )
      .map((key) => ({
        addendum: values.transactions[key].addendum,
        addendumEntityId: values.transactions[key].addendum ? '32' : 'no',
        amount: currencyToNumber(values.transactions[key].amount),
        contactId: values.transactions[key].id,
        paymentDetailId: values.transactions[key].account
          ? values.transactions[key].account[0].id
          : null,
        // Adding partTxnId for modify
        // Don't add partTxnId for copy as payment
        ...(action !== PAYMENT_ACTIONS.COPY
          ? { partTxnId: values.transactions[key].partTxnId }
          : [])
      }));
    const execute = () =>
      initiateSMBEmployeePayment$({
        achSenderId: values?.batch?.entity?.[0]?.id,
        batchDescription: values.batch.batchDescription,
        effectiveDate: forceServerTz(values.batch.effectiveEntryDate[0], serverTimezone),
        ...(APIData?.achCompany?.displayOffsetAccounts ||
        APIData?.paymentDetails?.displayOffsetAccounts
          ? {
              fromAccountId:
                values.batch.fromAccount[0].accountId || values.batch.fromAccount[0].id
            }
          : []),
        paymentName: values.batch.paymentName,
        smallBusinessEmployeePaymentTransactionRequest: payments,
        // Adding custTrace turns this into a modify instead of create
        ...(action !== PAYMENT_ACTIONS.COPY
          ? { custTraceNum: APIData.paymentDetails && APIData.paymentDetails.custTrace }
          : [])
      }).subscribe(({ data, success: apiSuccess }) => {
        const { errorMessage, limitWarning, success } = normalizeErrorResp({
          data,
          locale,
          success: apiSuccess
        });

        const titleMap = {
          limitWarning: locale['common.paymentLimitWarning'],
          create: locale['common.paymentCreated'],
          copy: locale['common.paymentCopied']
        };

        const title = limitWarning
          ? titleMap.limitWarning
          : titleMap[action] || locale['common.paymentModified'];

        setIsSubmitting(false);
        setConfirmation(null);
        if (!success) {
          return handleError({
            callback: () => execute(),
            data,
            errorMessage,
            locale,
            setChallengeManager,
            setMessage
          });
        }
        setChallengeManager(null);
        onChange('allowNavigation', true);
        setReduxMessage({
          details: `${locale['employee.payment.customerTraceNumber']}: ${data.trace}. ${limitWarning}`,
          success: !limitWarning,

          // TODO: API needs to tell me what the approved status is.
          title: generateApprovedTitle(locale)(title, data?.approvalRequirement),

          type: locale['employee.payment.smallBusiness']
        });
        setSmbHighlightedRows([
          {
            bankTrace: data.trace,
            custTrace: data.trace,
            successHighlighting: true
          }
        ]);
        return history.push({
          pathname: WORKSPACES.PYMTCNTR,
          state: {}
        });
      }, handleServerError);
    if (confirmation) return execute();
    return setConfirmation({
      Component: (
        <ConfirmModal
          action={submitType}
          actionTitle={locale['common.sendPayment']}
          actionCallback={execute}
          apiData={APIData}
          formState={values}
          locale={locale}
          onHide={() => {
            setConfirmation(null);
            setIsSubmitting(false);
            setChallengeManager(false);
          }}
          totalAmount={totalAmount}
          classes={classes}
        />
      ),
      variant: 'error'
    });
  }
  // No matching submitType found. Should never happen
  return null;
};

// Used to populate form values for modify and view
export const loadPaymentData = ({ dateSettings, onChange, payees, paymentDetails }) => {
  const start = moment.parseZone(dateSettings.startDate);
  const effectiveEntryDate = moment.parseZone(paymentDetails.effectiveEntryDate);
  const date = effectiveEntryDate.isBefore(start) ? start : effectiveEntryDate;

  const getTransactionsWithPayee = () => ({
    ...paymentDetails.smallBusinessEmployeePaymentTransactionResponse
      // Filter out transactions with missing payees
      .filter((transaction) => payees.find((payee) => payee.id === transaction.contactId))
      .map((transaction) => {
        // Find out which property matches payee to transaction
        const payee = payees.find((payee) => payee.id === transaction.contactId);
        // Find matching paymentDetail for account
        let account = payee.paymentDetail.find(
          (account) => account.id === transaction.paymentDetailId
        );
        // If there is no matching account is only one account to choose from then pre-populate this field
        if (!account && payee.paymentDetail && payee.paymentDetail.length === 1) {
          account = payee.paymentDetail[0];
        }
        return {
          account: account
            ? [
                {
                  ...account,
                  name: account.accountNumber
                }
              ]
            : null,
          ...(payee || []),
          nickNameTrunc: truncate(payee.nickName, { length: 15, separator: ' ' }),
          ...transaction,
          amount: numberToDecimal(transaction.amount)
        };
      })
  });
  const getTransactionsWithoutPayee = () => ({
    ...paymentDetails?.smallBusinessEmployeePaymentTransactionResponse?.map((transaction) => {
      return {
        account: transaction
          ? [
              {
                ...transaction,
                accountNumber: transaction?.beneficiaryAccountNumber
              }
            ]
          : null,
        nickNameTrunc: transaction?.beneficiaryId,
        ...transaction,
        amount: numberToDecimal(transaction.amount)
      };
    })
  });

  onChange(FORM_SECTIONS.BATCH, {
    batchDescription: paymentDetails?.batchDescription,
    effectiveEntryDate: [date, date],
    entity: [
      {
        id: paymentDetails?.achSenderId,
        name: paymentDetails?.achSenderName
      }
    ],
    paymentName: paymentDetails?.paymentName,
    status: paymentDetails?.status,
    viewDate: [effectiveEntryDate, effectiveEntryDate],
    // If offsetLevel is set to batch then populate this offset account field
    ...(paymentDetails?.displayOffsetAccounts
      ? {
          fromAccount: [
            {
              id: paymentDetails?.fromAccountId,
              name: `${paymentDetails?.fromAccountNickName || paymentDetails?.fromAccountName} - ${
                paymentDetails?.fromAccountNumber
              }`
            }
          ]
        }
      : [])
  });

  const hasPayees = paymentDetails?.smallBusinessEmployeePaymentTransactionResponse?.filter(
    (transaction) => payees.find((payee) => payee.id === transaction.contactId)
  );

  onChange(
    FORM_SECTIONS.TRANSACTIONS,
    !isEmpty(hasPayees) ? getTransactionsWithPayee() : getTransactionsWithoutPayee()
  );
};

function normalizeDates(data) {
  // Server businessDaysOfWeek start at Sun - 1, Mon - 2, Tues - 3, Wed - 4, Thurs - 5, Fri - 6, Sat - 7
  // NOTE: adding a day here because moment starts Sun at 0 instead
  const {
    achCalendarResponse: {
      earliestDayForACH = {},
      latestDayForACH = {},
      holidays = {},
      businessDaysOfWeek = {}
    } = {}
  } = data?.data;
  const customBusinessDaysOfWeek = [...Array(7)].map((_, i) => businessDaysOfWeek.includes(i + 1));

  return {
    businessDaysOfWeek: customBusinessDaysOfWeek,
    defaultDate: earliestDayForACH,
    endDate: latestDayForACH,
    holidays,
    startDate: earliestDayForACH
  };
}
function getDateSettings({ action, approvedPayees, paymentDetails }) {
  // Don't ask me why we use a different date API for modify and copy.
  // Seriously, don't ask. I have no clue just following orders.
  if ([PAYMENT_ACTIONS.MODIFY, PAYMENT_ACTIONS.VIEW, PAYMENT_ACTIONS.COPY].includes(action)) {
    return getACHPaymentSettings$({
      achSenderId: paymentDetails?.data?.achSenderId,
      paymentType: PAYMENT_TYPES.ACH.EMP_CO
    }).pipe(map(normalizeDates));
  }

  return getDates({
    action: PAYMENT_ACTIONS.EMPLOYEE.toUpperCase(),
    payeeId: approvedPayees?.data?.[0]?.id,
    paymentTypes: [PAYMENT_TYPES.ACH.EMP_CO]
  });
}

// NOTE: small business payments only use the first ach company returned from this.
// NOTE: if there are no ach companies then display error page.
export const loadData = ({ action, custTraceNum, onChange, setAPIData }) =>
  zip(
    getAchCompanyForPaymentType$({
      isFreeform: true,
      paymentType: PAYMENT_TYPES.ACH.EMP_CO
    }),
    getApprovedEmployeePayees$(),
    [PAYMENT_ACTIONS.MODIFY, PAYMENT_ACTIONS.VIEW, PAYMENT_ACTIONS.COPY].includes(action)
      ? retrieveSMBEmployeePaymentDetails$({ custTraceNum })
      : of([])
  )
    .pipe(
      mergeMap(([{ data, success }, approvedPayees, paymentDetails]) =>
        forkJoin({
          companies: of({ data, success }),
          // If no ACH companies then don't make these calls and show error page
          ...(data && data.length > 0
            ? {
                dateSettings: getDateSettings({
                  action,
                  approvedPayees,
                  data,
                  paymentDetails
                }),
                payees: of(approvedPayees),
                paymentTypes: getSMBPaymentTypes$()
              }
            : []),
          // If user is on modify, view, or copying a payment then load payment details
          ...([PAYMENT_ACTIONS.MODIFY, PAYMENT_ACTIONS.VIEW, PAYMENT_ACTIONS.COPY].includes(action)
            ? { paymentDetails: of(paymentDetails) }
            : [])
        })
      )
    )
    .subscribe(
      ({ companies, dateSettings = null, payees, paymentDetails = null, paymentTypes }) => {
        // TODO: handle success false
        // NOTE: Component waits for APIData to not be empty before displaying so make form changes first
        // If no companies then show error page
        if (!companies || !companies.data || companies.data.length === 0) {
          onChange(FORM_SECTIONS.BATCH, {});
          return setAPIData({
            error: true
          });
        }
        const matchingPaymentType = paymentTypes.data.ACH.find(
          (paymentType) => paymentType.code === PAYMENT_TYPES.ACH.EMP_CO
        );
        if (action === PAYMENT_ACTIONS.CREATE) {
          onChange(FORM_SECTIONS.BATCH, {
            effectiveEntryDate: [
              moment.parseZone(dateSettings.startDate),
              moment.parseZone(dateSettings.startDate)
            ],
            paymentName: '',
            batchDescription: ''
          });
          onChange(FORM_SECTIONS.TRANSACTIONS, {
            ...payees.data.map((payee) => ({
              ...payee,
              addendum: '',
              amount: '0.00',
              beneficiary: payee.name,
              fromAccountNumber: '',
              nickNameTrunc: truncate(payee.nickName, { length: 15, separator: ' ' }),
              // If there is only one paymentDetail then pre-populate it
              ...(payee.paymentDetail.length === 1
                ? {
                    account: [
                      {
                        ...payee.paymentDetail[0],
                        name: payee.paymentDetail[0].accountNumber
                      }
                    ]
                  }
                : [])
            }))
          });
        }
        if (
          [PAYMENT_ACTIONS.MODIFY, PAYMENT_ACTIONS.VIEW, PAYMENT_ACTIONS.COPY].includes(action)
        ) {
          loadPaymentData({
            dateSettings,
            onChange,
            payees: payees.data,
            paymentDetails: paymentDetails.data
          });
        }
        return setAPIData({
          achCompanies: companies.data.map((company) => {
            const offsetAccount = company.offsetAccount
              .map((account) => ({
                ...account,
                id: account.accountId,
                name: `${account.accountNickName || account.accountName} - ${
                  account.accountNumber
                }`
              }))
              .sort((a, b) => (a.name > b.name ? 1 : -1));
            return {
              ...company,
              offsetAccount
            };
          }),
          achCompany: {
            ...companies.data[0],
            offsetAccount: companies.data[0].offsetAccount
              .map((account) => ({
                ...account,
                id: account.accountId,
                name: `${account.accountNickName || account.accountName} - ${
                  account.accountNumber
                }`
              }))
              .sort((a, b) => (a.name > b.name ? 1 : -1))
          },
          dateSettings,
          offsetLevel: matchingPaymentType ? matchingPaymentType.offsetLevel : 'B',
          payees: payees.data,
          paymentDetails: paymentDetails ? paymentDetails.data : null
        });
        // TODO: handle server error
      }
    );
